import { CertificateType, CertificationFormVM } from 'civil-status/view-models/document-birth-form';
import { useFormikContext } from 'formik';
import { memo, useCallback, useMemo } from 'react';
import DocumentVisualizationBirthForm from './birth-data-form';
import DocumentVisualizationCitizenshipForm from './citizenship-data-form';
import DocumentVisualizationCivilUnionForm from './civil-union-data-form';
import DocumentVisualizationDeathForm from './death-data-form';
import DocumentVisualizationMarriageForm from './marriage-data-form';
import DocumentVisualizationANSCForm from './ansc-visualization';

interface DocumentVisualizationPageProps {
  onBack: () => void;
  onNext: () => void;
}

const DocumentVisualizationPage = ({
  onBack: onBackExternal,
  onNext,
}: DocumentVisualizationPageProps): React.ReactElement => {
  const formContext = useFormikContext<CertificationFormVM>();

  const onBack = useCallback(() => {
    formContext.setValues({
      ...formContext.values,
      documentChosenBirth: null,
      documentChosenCitizenship: null,
      documentChosenCivilUnion: null,
      documentChosenDeath: null,
      documentChosenMarriage: null,
      chosenDocument: null,
    });
    onBackExternal();
  }, [formContext.values, formContext.setValues]);

  const isNascita: boolean = useMemo(() => {
    return formContext.values.chosenDocument?.element?.codiceAtto === CertificateType.nascita;
  }, [formContext.values.chosenDocument?.element?.codiceAtto]);

  const isMorte: boolean = useMemo(() => {
    return formContext.values.chosenDocument?.element?.codiceAtto === CertificateType.morte;
  }, [formContext.values.chosenDocument?.element?.codiceAtto]);

  const isMatrimonio: boolean = useMemo(() => {
    return formContext.values.chosenDocument?.element?.codiceAtto === CertificateType.matrimonio;
  }, [formContext.values.chosenDocument?.element?.codiceAtto]);

  const isUnioneCivile: boolean = useMemo(() => {
    return formContext.values.chosenDocument?.element?.codiceAtto === CertificateType.unioneCivile;
  }, [formContext.values.chosenDocument?.element?.codiceAtto]);

  const isCittadinanza: boolean = useMemo(() => {
    return formContext.values.chosenDocument?.element?.codiceAtto === CertificateType.cittadinanza;
  }, [formContext.values.chosenDocument?.element?.codiceAtto]);

  if (formContext.values.chosenDocument?.isAnsc) {
    return <DocumentVisualizationANSCForm onBack={onBack} />;
  }

  if (isNascita) {
    return (
      <DocumentVisualizationBirthForm
        onBack={onBack}
        onNext={async (values) => {
          await formContext.setFieldValue('documentChosenBirth', values);
          onNext();
        }}
        type="N"
      />
    );
  }

  if (isMorte) {
    return (
      <DocumentVisualizationDeathForm
        onBack={onBack}
        onNext={async (values) => {
          await formContext.setFieldValue('documentChosenDeath', values);
          onNext();
        }}
        type="D"
      />
    );
  }

  if (isMatrimonio) {
    return (
      <DocumentVisualizationMarriageForm
        onBack={onBack}
        onNext={async (values) => {
          await formContext.setFieldValue('documentChosenMarriage', values);
          onNext();
        }}
        type="M"
      />
    );
  }

  if (isUnioneCivile) {
    return (
      <DocumentVisualizationCivilUnionForm
        onBack={onBack}
        onNext={async (values) => {
          await formContext.setFieldValue('documentChosenCivilUnion', values);
          onNext();
        }}
        type="U"
      />
    );
  }

  if (isCittadinanza) {
    return (
      <DocumentVisualizationCitizenshipForm
        onBack={onBack}
        onNext={async (values) => {
          await formContext.setFieldValue('documentChosenCitizenship', values);
          onNext();
        }}
        type="C" // verificare
      />
    );
  }

  return <></>;
};

DocumentVisualizationPage.displayName = 'Document Visualization Page';

const DocumentVisualization = memo(DocumentVisualizationPage);

export default DocumentVisualization;
